'use client';

import clsx from 'clsx';
import Link from 'next/link';
import { useMemo, useState } from 'react';

type DomainId =
  | 'foundations'
  | 'identity'
  | 'endpoint'
  | 'detection'
  | 'resilience'
  | 'governance';

type Severity = 'critical' | 'high' | 'medium' | 'low';

type Option = {
  value: string;
  label: string;
  score: number;
  description: string;
  remediation?: {
    summary: string;
    action: string;
    link?: {
      label: string;
      href: string;
    };
  };
};

type Question = {
  id: string;
  domain: DomainId;
  prompt: string;
  helper?: string;
  options: Option[];
};

type AnswerMap = Record<string, string>;

type DomainMeta = {
  title: string;
  description: string;
  icon: string;
};

const domainMeta: Record<DomainId, DomainMeta> = {
  foundations: {
    title: 'Security Foundations',
    description: 'Asset visibility, baseline policies, and zero-trust guardrails.',
    icon: '🧭',
  },
  identity: {
    title: 'Identity & Access',
    description: 'Privileged access, MFA coverage, and account lifecycle controls.',
    icon: '🔐',
  },
  endpoint: {
    title: 'Endpoint Hardening',
    description: 'Patch hygiene, device management, and configuration enforcement.',
    icon: '💻',
  },
  detection: {
    title: 'Detection & Response',
    description: 'Logging, telemetry coverage, and response orchestration.',
    icon: '📡',
  },
  resilience: {
    title: 'Resilience & Recovery',
    description: 'Backups, tabletop exercises, and incident communications.',
    icon: '🛡️',
  },
  governance: {
    title: 'Governance & Culture',
    description: 'Leadership accountability, training cadence, and vendor oversight.',
    icon: '🏛️',
  },
};

const severityFromScore = (score: number): Severity => {
  if (score === 0) return 'critical';
  if (score === 1) return 'high';
  if (score === 2) return 'medium';
  return 'low';
};

const questions: Question[] = [
  {
    id: 'asset-inventory',
    domain: 'foundations',
    prompt: 'How accurate is your real-time inventory of endpoints, servers, SaaS applications, and shadow IT?',
    helper:
      'Accurate inventories underpin patching, zero trust, and incident response scopes.',
    options: [
      {
        value: 'automated',
        label: 'Continuously discovered and reconciled via automated tooling (CMDB/EDR/SaaS posture).',
        score: 3,
        description: 'Assets sync automatically within 24 hours of change and feed governance dashboards.',
      },
      {
        value: 'partial',
        label: 'Mostly tracked, but some teams rely on quarterly exports or spreadsheets.',
        score: 2,
        description: 'Critical systems are known, but gaps exist for SaaS apps or remote endpoints.',
        remediation: {
          summary: 'Inventory gaps hide risk exposure.',
          action: 'Deploy automated discovery (EDR, MDM, SaaS management) and integrate updates into a central CMDB.',
          link: {
            label: 'Explore our Asset Intelligence Accelerator',
            href: '/services/24-7-detection-and-response',
          },
        },
      },
      {
        value: 'ad-hoc',
        label: 'Only refreshed during audits or major projects; many devices are undocumented.',
        score: 1,
        description: 'Shadow IT, contractor devices, or legacy systems appear unexpectedly during incidents.',
        remediation: {
          summary: 'Lack of visibility undermines every control downstream.',
          action: 'Stand up lightweight discovery (network scans, identity inventory) and create onboarding/offboarding guardrails.',
        },
      },
      {
        value: 'unknown',
        label: 'We do not maintain a reliable inventory today.',
        score: 0,
        description: 'Response teams cannot scope incidents or patching effectively.',
        remediation: {
          summary: 'Foundational control missing.',
          action: 'Launch an asset baseline project and pair it with device enrollment policies before pursuing advanced controls.',
        },
      },
    ],
  },
  {
    id: 'policy-baseline',
    domain: 'foundations',
    prompt: 'Do you have enforceable baseline policies (admin rights, hardening templates, data classification)?',
    helper: 'Policies must be actionable guardrails with technical enforcement, not shelfware.',
    options: [
      {
        value: 'enforced',
        label: 'Baselines exist, mapped to CIS/NIST, and enforced via automation (GPO, MDM, IaC).',
        score: 3,
        description: 'Drift is detected automatically, and exceptions require documented approvals.',
      },
      {
        value: 'documented',
        label: 'Policies exist and are reviewed annually, but enforcement is mostly manual.',
        score: 2,
        description: 'Teams rely on checklists and spot checks instead of continuous control monitoring.',
        remediation: {
          summary: 'Manual enforcement leads to drift.',
          action: 'Map policies to technical controls—add configuration baselines and policy-as-code to remove human bottlenecks.',
        },
      },
      {
        value: 'emerging',
        label: 'We have draft policies or templates, but they are not widely communicated.',
        score: 1,
        description: 'Different teams interpret standards differently, resulting in inconsistent controls.',
        remediation: {
          summary: 'Policy maturity stalled at design stage.',
          action: 'Publish minimum standards, socialize them via onboarding, and establish an approval workflow for exceptions.',
        },
      },
      {
        value: 'none',
        label: 'No formalized baseline policies today.',
        score: 0,
        description: 'Security posture depends on individual contributors and tribal knowledge.',
        remediation: {
          summary: 'Baseline missing—start here.',
          action: 'Prioritize a lightweight security charter and baseline policy pack aligned to CIS IG1 controls.',
        },
      },
    ],
  },
  {
    id: 'mfa-coverage',
    domain: 'identity',
    prompt: 'What percentage of identities (human, privileged, and service) are protected with phishing-resistant MFA?',
    helper: 'Attackers target the weakest credential—coverage must include contractors, admins, and APIs.',
    options: [
      {
        value: 'complete',
        label: '100% of human identities and privileged access use phishing-resistant MFA with continuous attestation.',
        score: 3,
        description: 'Hardware keys or number matching protect both workforce and administrative access.',
      },
      {
        value: 'most',
        label: '80-99% coverage; a few legacy systems or service accounts still rely on passwords.',
        score: 2,
        description: 'Exceptions exist for break-glass or technical debt that is being remediated.',
        remediation: {
          summary: 'Residual MFA gaps invite credential stuffing.',
          action: 'Implement conditional access policies and phase out basic auth for remaining systems.',
        },
      },
      {
        value: 'partial',
        label: 'Core workforce uses MFA, but admins and service accounts rely on passwords or app passwords.',
        score: 1,
        description: 'Privileged escalation paths remain vulnerable to phishing and password spraying.',
        remediation: {
          summary: 'High-risk identities lack MFA.',
          action: 'Introduce privileged identity management and rotate service accounts to certificate or key-based auth.',
          link: {
            label: 'See how we harden privileged identities',
            href: '/services/small-business-cybersecurity',
          },
        },
      },
      {
        value: 'minimal',
        label: 'MFA is optional or limited to a single application.',
        score: 0,
        description: 'Phishing a single credential can unlock the environment.',
        remediation: {
          summary: 'Immediate MFA rollout required.',
          action: 'Adopt an MFA platform with staged rollout, enforce for admins first, and expand to all users within 30 days.',
        },
      },
    ],
  },
  {
    id: 'privileged-access',
    domain: 'identity',
    prompt: 'How do you govern privileged access elevation and dormant admin accounts?',
    options: [
      {
        value: 'just-in-time',
        label: 'We use just-in-time elevation with approval workflows and session recording.',
        score: 3,
        description: 'Standing privileges are eliminated, and every elevation is auditable.',
      },
      {
        value: 'tiered',
        label: 'Privileged groups are tiered, reviewed quarterly, and dormant accounts are disabled automatically.',
        score: 2,
        description: 'Manual reviews occur, but some shared admin accounts remain.',
        remediation: {
          summary: 'Shared accounts erode accountability.',
          action: 'Introduce PIM/PAM tooling, remove shared credentials, and automate quarterly access certification.',
        },
      },
      {
        value: 'basic',
        label: 'Admins use separate accounts, but reviews are ad-hoc and approvals live in email threads.',
        score: 1,
        description: 'Dormant elevated access may persist for months.',
        remediation: {
          summary: 'Privileged lifecycle unmanaged.',
          action: 'Stand up a lightweight approval queue and tie it to ticketing for traceability.',
        },
      },
      {
        value: 'standing',
        label: 'Privileged access is permanent once granted.',
        score: 0,
        description: 'Attackers gain broad access if a single admin credential is compromised.',
        remediation: {
          summary: 'Standing admin access is a critical risk.',
          action: 'Segment admin accounts immediately and require temporary elevation with MFA.',
        },
      },
    ],
  },
  {
    id: 'patching-cadence',
    domain: 'endpoint',
    prompt: 'What does your patch management lifecycle look like for endpoints and servers?',
    helper: 'Unpatched systems remain the top entry point in ransomware investigations.',
    options: [
      {
        value: 'automated',
        label: 'Automated patch orchestration with enforcement SLAs (≤7 days critical, ≤30 days others).',
        score: 3,
        description: 'Exceptions require risk acceptance and compensating controls.',
      },
      {
        value: 'scheduled',
        label: 'Monthly patch windows with health reporting, but enforcement relies on user compliance.',
        score: 2,
        description: 'High severity fixes can take weeks if devices are offline.',
        remediation: {
          summary: 'Manual patching leaves blind spots.',
          action: 'Adopt enforced maintenance windows, wake-on-LAN, and reboot orchestration for remote devices.',
        },
      },
      {
        value: 'best-effort',
        label: 'We push updates when time allows; reporting is inconsistent.',
        score: 1,
        description: 'Patch status is unknown until a breach assessment occurs.',
        remediation: {
          summary: 'No measurable SLA.',
          action: 'Centralize patch telemetry and set severity-based SLAs aligned to CIS IG1.',
        },
      },
      {
        value: 'unsupported',
        label: 'Legacy systems rarely receive updates.',
        score: 0,
        description: 'Attack surface expands every month with known exploits.',
        remediation: {
          summary: 'Unpatched legacy assets are critical exposures.',
          action: 'Isolate unsupported systems, virtualize where possible, and budget for modernization.',
        },
      },
    ],
  },
  {
    id: 'endpoint-controls',
    domain: 'endpoint',
    prompt: 'Which endpoint protection stack is deployed across laptops, servers, and mobile devices?',
    options: [
      {
        value: 'edr',
        label: 'Unified EDR/XDR with behavioral analytics and managed response coverage.',
        score: 3,
        description: 'Analysts receive correlated detections and remote containment controls.',
      },
      {
        value: 'hybrid',
        label: 'Modern EDR on critical systems; some business units still rely on legacy antivirus.',
        score: 2,
        description: 'Coverage gaps exist for BYOD or remote contractors.',
        remediation: {
          summary: 'Hybrid tooling reduces visibility.',
          action: 'Standardize on a single EDR platform and require enrollment for device access.',
          link: {
            label: 'Review our Endpoint Hardening Playbook',
            href: '/services/24-7-detection-and-response',
          },
        },
      },
      {
        value: 'antivirus',
        label: 'Traditional antivirus only; alert triage is manual.',
        score: 1,
        description: 'Advanced attacks bypass signature-based tools.',
        remediation: {
          summary: 'Legacy antivirus cannot keep pace with modern threats.',
          action: 'Plan a rapid migration to managed EDR with 24/7 monitoring.',
        },
      },
      {
        value: 'minimal',
        label: 'No standardized endpoint protection.',
        score: 0,
        description: 'Incident responders have no remote containment ability.',
        remediation: {
          summary: 'Immediate investment required.',
          action: 'Deploy baseline EDR/MDM tooling before expanding other initiatives.',
        },
      },
    ],
  },
  {
    id: 'logging',
    domain: 'detection',
    prompt: 'How centralized and actionable is your security logging and telemetry?',
    helper: 'Detection depends on correlated telemetry across identity, endpoint, and cloud workloads.',
    options: [
      {
        value: 'unified',
        label: 'Security data streams into a SIEM/SOAR with detections tuned to MITRE ATT&CK.',
        score: 3,
        description: 'Playbooks trigger automated investigation and alert fatigue is managed.',
      },
      {
        value: 'partial',
        label: 'Critical logs centralize, but SaaS or network telemetry still lives in separate consoles.',
        score: 2,
        description: 'Investigations require jumping between tools.',
        remediation: {
          summary: 'Fragmented telemetry slows response.',
          action: 'Expand log collection to SaaS APIs and create correlation rules for privilege changes and data exfil.',
        },
      },
      {
        value: 'basic-logs',
        label: 'Each platform retains its own logs; alerts are reviewed manually when time allows.',
        score: 1,
        description: 'Incidents go undetected without obvious symptoms.',
        remediation: {
          summary: 'No centralized detection capability.',
          action: 'Adopt a lightweight SIEM or managed detection service to consolidate telemetry.',
        },
      },
      {
        value: 'none',
        label: 'We only collect logs for compliance or troubleshooting.',
        score: 0,
        description: 'Threat hunting and incident scoping are impossible.',
        remediation: {
          summary: 'Logging must start immediately.',
          action: 'Enable platform-native logging, retain for 90+ days, and stream to a central collector.',
        },
      },
    ],
  },
  {
    id: 'playbooks',
    domain: 'detection',
    prompt: 'Do you maintain incident response playbooks with clearly defined roles and escalation paths?',
    options: [
      {
        value: 'tested',
        label: 'Playbooks exist for top threats, mapped to stakeholders, and tested quarterly.',
        score: 3,
        description: 'Executive comms, legal, and IR retainers are pre-coordinated.',
      },
      {
        value: 'documented',
        label: 'Playbooks exist but have not been exercised in the past 12 months.',
        score: 2,
        description: 'Plans rely on assumptions that may not hold during an actual incident.',
        remediation: {
          summary: 'Unvalidated playbooks fail under stress.',
          action: 'Schedule tabletop exercises covering ransomware, insider misuse, and SaaS compromise scenarios.',
        },
      },
      {
        value: 'outline',
        label: 'We have an informal checklist or escalation matrix.',
        score: 1,
        description: 'Key contacts or vendors may be out of date.',
        remediation: {
          summary: 'Playbooks lack depth.',
          action: 'Expand into detailed runbooks with decision trees and communications templates.',
        },
      },
      {
        value: 'none',
        label: 'No formal playbooks today.',
        score: 0,
        description: 'Incidents will be chaotic and slow to contain.',
        remediation: {
          summary: 'Document incident response fundamentals.',
          action: 'Create a basic playbook and align it to NIST 800-61 phases as a starting point.',
        },
      },
    ],
  },
  {
    id: 'backups',
    domain: 'resilience',
    prompt: 'How resilient are your backups against ransomware, insider threats, and cloud outages?',
    helper: 'Backups must be immutable, isolated, and routinely validated to guarantee recovery.',
    options: [
      {
        value: 'immutable',
        label: '3-2-1-1 strategy with immutable copies, MFA-protected consoles, and quarterly recovery testing.',
        score: 3,
        description: 'Recovery time and point objectives are validated and documented.',
      },
      {
        value: 'hardened',
        label: '3-2-1 backups with limited testing; immutability planned but not fully implemented.',
        score: 2,
        description: 'Recovery confidence depends on staff availability and manual validation.',
        remediation: {
          summary: 'Immutable tier still pending.',
          action: 'Add write-once storage or object lock and automate quarterly restore drills.',
          link: {
            label: 'See our Ransomware Readiness Accelerator',
            href: '/services/backup-and-ransomware-defense',
          },
        },
      },
      {
        value: 'traditional',
        label: 'Nightly backups stored onsite or in the same cloud account; testing is ad-hoc.',
        score: 1,
        description: 'A single credential compromise could delete every copy.',
        remediation: {
          summary: 'Backups vulnerable to ransomware.',
          action: 'Isolate copies offsite, add MFA to consoles, and script routine recovery validation.',
        },
      },
      {
        value: 'risky',
        label: 'Backups are inconsistent or non-existent.',
        score: 0,
        description: 'Business continuity depends on rebuilding systems from scratch.',
        remediation: {
          summary: 'Critical gap jeopardizing continuity.',
          action: 'Implement a managed backup platform immediately and define RPO/RTO targets with the business.',
        },
      },
    ],
  },
  {
    id: 'tabletop',
    domain: 'resilience',
    prompt: 'How often do you conduct cross-functional tabletop exercises covering cyber incidents?',
    options: [
      {
        value: 'quarterly',
        label: 'Quarterly exercises with IT, security, legal, PR, and executive leadership.',
        score: 3,
        description: 'Lessons learned feed process updates within 30 days.',
      },
      {
        value: 'annual',
        label: 'At least annually with IT/security leads and business stakeholders.',
        score: 2,
        description: 'Exercises focus on ransomware and data breach scenarios.',
        remediation: {
          summary: 'Increase tempo for high-impact threats.',
          action: 'Add mid-year micro-tabletops focused on phishing, supplier compromise, and SaaS outages.',
        },
      },
      {
        value: 'ad-hoc',
        label: 'Only when required by regulators or after major incidents.',
        score: 1,
        description: 'Response roles and communications remain untested.',
        remediation: {
          summary: 'Response muscle memory missing.',
          action: 'Plan recurring exercises with executive participation and update communications templates.',
        },
      },
      {
        value: 'never',
        label: 'We have never conducted a tabletop exercise.',
        score: 0,
        description: 'Crisis coordination will start from zero during an incident.',
        remediation: {
          summary: 'Tabletops build leadership confidence.',
          action: 'Schedule an introductory tabletop with a facilitator to identify obvious plan gaps.',
        },
      },
    ],
  },
  {
    id: 'training',
    domain: 'governance',
    prompt: 'What is the cadence and relevance of your security awareness training program?',
    options: [
      {
        value: 'role-based',
        label: 'Role-based training with quarterly micro-learning and phishing simulations.',
        score: 3,
        description: 'Metrics drive targeted coaching for high-risk groups.',
      },
      {
        value: 'annual',
        label: 'Annual training for all staff with periodic phishing tests.',
        score: 2,
        description: 'Content refreshed yearly but not tailored by role.',
        remediation: {
          summary: 'Training lacks personalization.',
          action: 'Introduce quarterly campaigns and provide executive-specific briefings on breach response.',
        },
      },
      {
        value: 'basic',
        label: 'Annual compliance module without simulated phishing.',
        score: 1,
        description: 'Users forget guidance quickly; reporting culture is weak.',
        remediation: {
          summary: 'Engagement is low.',
          action: 'Launch monthly phishing simulations and create a champions program inside each department.',
        },
      },
      {
        value: 'none',
        label: 'No formal training or phishing simulations.',
        score: 0,
        description: 'Employees cannot recognize or escalate suspicious activity.',
        remediation: {
          summary: 'People remain the largest attack surface.',
          action: 'Deploy an engaging security awareness platform immediately and track reporting rates.',
        },
      },
    ],
  },
  {
    id: 'vendor-risk',
    domain: 'governance',
    prompt: 'How do you assess and monitor third-party and SaaS vendor security posture?',
    options: [
      {
        value: 'continuous',
        label: 'Risk-tiered assessments, continuous monitoring, and contractual security obligations.',
        score: 3,
        description: 'Vendors receive remediation plans and attestations are tracked.',
      },
      {
        value: 'annual-review',
        label: 'Annual questionnaires for critical vendors; limited follow-up on findings.',
        score: 2,
        description: 'Evidence collection is manual, and monitoring stops after onboarding.',
        remediation: {
          summary: 'Static questionnaires miss emerging risk.',
          action: 'Adopt continuous monitoring signals (attack surface, breach alerts) for top-tier suppliers.',
        },
      },
      {
        value: 'basic-due-diligence',
        label: 'Contract language references security, but due diligence is lightweight.',
        score: 1,
        description: 'Security documents are collected but rarely reviewed.',
        remediation: {
          summary: 'Vendor controls are assumed, not verified.',
          action: 'Create a risk scoring rubric and require remediation commitments before contract signing.',
        },
      },
      {
        value: 'none',
        label: 'We rely on vendor reputation and assume they manage security.',
        score: 0,
        description: 'Third-party incidents will cascade into your environment unchecked.',
        remediation: {
          summary: 'Vendor risk unmanaged.',
          action: 'Establish at least a critical vendor register and pre-contract security checklist.',
        },
      },
    ],
  },
];

const maturityBands = [
  {
    min: 0,
    max: 39,
    label: 'Level 1: Reactive',
    description: 'Controls are informal and heavily manual. Incidents would overwhelm the team.',
  },
  {
    min: 40,
    max: 59,
    label: 'Level 2: Developing',
    description: 'Foundational controls exist, but automation and validation are inconsistent.',
  },
  {
    min: 60,
    max: 79,
    label: 'Level 3: Progressive',
    description: 'Security is aligned to frameworks with measurable SLAs and executive visibility.',
  },
  {
    min: 80,
    max: 100,
    label: 'Level 4: Advanced',
    description: 'Controls are automated, validated, and tightly integrated with business priorities.',
  },
];

const severityOrder: Record<Severity, number> = {
  critical: 0,
  high: 1,
  medium: 2,
  low: 3,
};

export function CybersecurityBaselineAssessment() {
  const [stage, setStage] = useState<'intro' | 'quiz' | 'results'>('intro');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<AnswerMap>({});

  const currentQuestion = questions[currentIndex];
  const totalQuestions = questions.length;
  const answeredCount = Object.keys(answers).length;
  const progressPercent = Math.round((answeredCount / totalQuestions) * 100);

  const maxScore = totalQuestions * 3;

  const score = useMemo(() => {
    return questions.reduce((total, question) => {
      const answer = answers[question.id];
      if (!answer) return total;
      const option = question.options.find((opt) => opt.value === answer);
      return total + (option?.score ?? 0);
    }, 0);
  }, [answers]);

  const scorePercent = Math.round((score / maxScore) * 100) || 0;

  const maturity = useMemo(() => {
    return (
      maturityBands.find((band) => scorePercent >= band.min && scorePercent <= band.max) ?? maturityBands[0]
    );
  }, [scorePercent]);

  const domainSummaries = useMemo(() => {
    return Object.entries(domainMeta).map(([domainId, meta]) => {
      const domainQuestions = questions.filter((question) => question.domain === domainId);
      const domainMax = domainQuestions.length * 3;
      const domainScore = domainQuestions.reduce((total, question) => {
        const answer = answers[question.id];
        if (!answer) return total;
        const option = question.options.find((opt) => opt.value === answer);
        return total + (option?.score ?? 0);
      }, 0);
      const domainPercent = domainMax === 0 ? 0 : Math.round((domainScore / domainMax) * 100);
      return {
        id: domainId as DomainId,
        ...meta,
        score: domainScore,
        max: domainMax,
        percent: domainPercent,
      };
    });
  }, [answers]);

  const prioritizedRecommendations = useMemo(() => {
    const items = questions.flatMap((question) => {
      const answer = answers[question.id];
      if (!answer) return [];
      const option = question.options.find((opt) => opt.value === answer);
      if (!option || option.score === 3 || !option.remediation) return [];
      const severity = severityFromScore(option.score);
      return [
        {
          id: question.id,
          domain: question.domain,
          question: question.prompt,
          severity,
          summary: option.remediation.summary,
          action: option.remediation.action,
          link: option.remediation.link,
        },
      ];
    });
    return items
      .sort((a, b) => {
        const severityCompare = severityOrder[a.severity] - severityOrder[b.severity];
        if (severityCompare !== 0) return severityCompare;
        return a.domain.localeCompare(b.domain);
      })
      .slice(0, 6);
  }, [answers]);

  const resetAssessment = () => {
    setStage('intro');
    setAnswers({});
    setCurrentIndex(0);
  };

  const startAssessment = () => {
    setStage('quiz');
  };

  const goToPrevious = () => {
    setCurrentIndex((prev) => Math.max(prev - 1, 0));
  };

  const handleOptionSelect = (questionId: string, value: string) => {
    setAnswers((prev) => {
      const updated: AnswerMap = { ...prev, [questionId]: value };
      return updated;
    });
    setCurrentIndex((prev) => {
      const nextIndex = prev + 1;
      if (nextIndex >= totalQuestions) {
        setStage('results');
        return prev; // keep pointer at last question for potential review
      }
      return nextIndex;
    });
  };

  const revisitQuestion = (questionId: string) => {
    const index = questions.findIndex((question) => question.id === questionId);
    if (index >= 0) {
      setStage('quiz');
      setCurrentIndex(index);
    }
  };

  return (
    <div className="bg-[#0f172a] text-white rounded-3xl p-8 md:p-10 shadow-xl">
      {stage === 'intro' && (
        <div className="space-y-6">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-white/10 text-2xl">🧪</span>
            <div>
              <h4 className="text-xl font-semibold uppercase tracking-wide text-white/70">Interactive Tool</h4>
              <h3 className="text-3xl md:text-4xl font-bold">Cybersecurity Baseline Self-Assessment</h3>
            </div>
          </div>
          <p className="text-white/80 text-lg leading-relaxed">
            Replace static checklists with a rapid, diagnostic experience. In about five minutes you’ll benchmark your controls
            against CIS Implementation Group 1 priorities, expose blind spots, and receive targeted remediation steps. No
            downloads or email gates—just real guidance you can act on today.
          </p>
          <div className="grid md:grid-cols-3 gap-6 text-left">
            <div className="bg-white/5 rounded-2xl p-5">
              <span className="text-3xl" aria-hidden>⏱️</span>
              <h5 className="text-lg font-semibold mt-3">5-Minute Diagnostic</h5>
              <p className="text-sm text-white/70 mt-2">Answer 12 questions spanning identity, detection, resilience, and governance.</p>
            </div>
            <div className="bg-white/5 rounded-2xl p-5">
              <span className="text-3xl" aria-hidden>🎯</span>
              <h5 className="text-lg font-semibold mt-3">Weighted Scoring</h5>
              <p className="text-sm text-white/70 mt-2">High-risk gaps trigger priority actions mapped to CIS, NIST, and real-world breaches.</p>
            </div>
            <div className="bg-white/5 rounded-2xl p-5">
              <span className="text-3xl" aria-hidden>🤝</span>
              <h5 className="text-lg font-semibold mt-3">Actionable Follow-Up</h5>
              <p className="text-sm text-white/70 mt-2">Get curated playbooks and service pathways to close the gaps you uncover.</p>
            </div>
          </div>
          <button
            type="button"
            onClick={startAssessment}
            className="inline-flex items-center justify-center w-full md:w-auto px-8 py-3 rounded-full text-lg font-semibold bg-white text-[#0f172a] hover:bg-slate-100 transition-colors"
          >
            Start the Self-Assessment
          </button>
        </div>
      )}

      {stage === 'quiz' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between text-sm text-white/70">
            <span>
              Question {currentIndex + 1} of {totalQuestions}
            </span>
            <span>{progressPercent}% complete</span>
          </div>
          <div className="h-2 bg-white/10 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[#2cc3d1] to-[#8b5cf6]"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          <div className="bg-white text-[#0f172a] rounded-2xl p-6 md:p-8 shadow-lg">
            <div className="flex items-start gap-3">
              <span className="text-3xl" aria-hidden>{domainMeta[currentQuestion.domain].icon}</span>
              <div>
                <p className="text-sm font-semibold text-[#6366f1] uppercase tracking-wide mb-2">
                  {domainMeta[currentQuestion.domain].title}
                </p>
                <h4 className="text-2xl font-bold mb-3">{currentQuestion.prompt}</h4>
                {currentQuestion.helper && (
                  <p className="text-gray-600 mb-5">{currentQuestion.helper}</p>
                )}
              </div>
            </div>

            <div className="space-y-4">
              {currentQuestion.options.map((option) => {
                const selectedValue = answers[currentQuestion.id];
                const isSelected = selectedValue === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleOptionSelect(currentQuestion.id, option.value)}
                    className={clsx(
                      'w-full text-left border rounded-2xl p-5 transition-all',
                      isSelected
                        ? 'border-[#2813e8] bg-[#eef2ff] shadow-lg'
                        : 'border-gray-200 hover:border-[#6366f1] hover:bg-slate-50'
                    )}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-semibold text-[#111827]">{option.label}</p>
                        <p className="text-sm text-gray-600 mt-2">{option.description}</p>
                      </div>
                      {isSelected && <span className="text-[#2813e8] font-semibold">Selected</span>}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex justify-between items-center text-sm text-white/70">
            <button
              type="button"
              onClick={goToPrevious}
              disabled={currentIndex === 0}
              className={clsx(
                'px-4 py-2 rounded-full border border-white/40 transition-colors',
                currentIndex === 0
                  ? 'opacity-40 cursor-not-allowed'
                  : 'hover:bg-white/10'
              )}
            >
              ← Back
            </button>
            <p className="text-xs md:text-sm">Selections auto-save. Change an answer anytime.</p>
          </div>
        </div>
      )}

      {stage === 'results' && (
        <div className="space-y-8">
          <div className="bg-white text-[#0f172a] rounded-3xl p-6 md:p-8 shadow-2xl">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div>
                <p className="text-sm font-semibold text-[#6366f1] uppercase tracking-wide">Your Cybersecurity Baseline</p>
                <h3 className="text-3xl font-bold mt-2">{scorePercent}% Alignment</h3>
                <p className="text-lg text-gray-600 mt-3">
                  {maturity.label} · {maturity.description}
                </p>
                <div className="flex flex-wrap gap-3 mt-4 text-sm text-gray-600">
                  <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#eef2ff] text-[#3730a3]">
                    🧮 Score {score} / {maxScore}
                  </span>
                  <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#dcfce7] text-[#166534]">
                    ✅ {answeredCount} controls evaluated
                  </span>
                </div>
              </div>
              <div className="md:text-right space-y-3 text-sm text-gray-600">
                <p>
                  Ready to close the gaps?{' '}
                  <Link href="/contact" className="text-[#2813e8] font-semibold hover:underline">
                    Talk with Inventive HQ security engineers
                  </Link>
                  .
                </p>
                <button
                  type="button"
                  onClick={resetAssessment}
                  className="inline-flex items-center justify-center px-5 py-2 rounded-full border border-[#2813e8] text-[#2813e8] font-semibold hover:bg-[#2813e8] hover:text-white transition-colors"
                >
                  Start Over
                </button>
              </div>
            </div>
          </div>

          <div>
            <h4 className="text-xl font-semibold mb-4">Domain Breakdown</h4>
            <div className="grid gap-4 md:grid-cols-2">
              {domainSummaries.map((domain) => (
                <button
                  key={domain.id}
                  type="button"
                  onClick={() => revisitQuestion(questions.find((q) => q.domain === domain.id)?.id ?? '')}
                  className="text-left bg-white/10 hover:bg-white/15 border border-white/10 rounded-2xl p-5 transition-colors"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm uppercase tracking-wide text-white/70">{domain.icon} {domain.title}</p>
                      <p className="text-lg font-semibold mt-1">{domain.percent}% maturity</p>
                      <p className="text-sm text-white/70 mt-2">{domain.description}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-white/60">
                        {domain.score} / {domain.max}
                      </p>
                    </div>
                  </div>
                  <div className="mt-4 h-2 bg-white/10 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-[#2cc3d1] to-[#8b5cf6]"
                      style={{ width: `${domain.percent}%` }}
                    />
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-xl font-semibold mb-4">Top Remediation Priorities</h4>
            {prioritizedRecommendations.length === 0 ? (
              <p className="text-white/70 text-sm bg-white/5 rounded-2xl p-5">
                Fantastic work—your responses indicate mature controls across every domain. Continue monitoring for drift and
                schedule quarterly reviews to maintain momentum.
              </p>
            ) : (
              <div className="space-y-4">
                {prioritizedRecommendations.map((item) => (
                  <div key={item.id} className="bg-white text-[#0f172a] rounded-2xl p-5 shadow-lg">
                    <div className="flex items-start gap-3">
                      <span className="text-2xl" aria-hidden>
                        {domainMeta[item.domain].icon}
                      </span>
                      <div className="space-y-2">
                        <div className="flex flex-wrap items-center gap-3">
                          <p className="text-sm font-semibold text-[#6366f1] uppercase tracking-wide">
                            {domainMeta[item.domain].title}
                          </p>
                          <span
                            className={clsx(
                              'inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold',
                              item.severity === 'critical' && 'bg-rose-100 text-rose-700',
                              item.severity === 'high' && 'bg-amber-100 text-amber-700',
                              item.severity === 'medium' && 'bg-sky-100 text-sky-700',
                              item.severity === 'low' && 'bg-emerald-100 text-emerald-700'
                            )}
                          >
                            {item.severity === 'critical' && 'Critical'}
                            {item.severity === 'high' && 'High Priority'}
                            {item.severity === 'medium' && 'Medium'}
                            {item.severity === 'low' && 'On Track'}
                          </span>
                        </div>
                        <p className="text-lg font-semibold">{item.summary}</p>
                        <p className="text-sm text-gray-600">{item.action}</p>
                        {item.link && (
                          <Link href={item.link.href} className="inline-flex items-center gap-2 text-sm font-semibold text-[#2813e8] hover:underline">
                            {item.link.label} →
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default CybersecurityBaselineAssessment;
